import { useStyles } from './AiGeneratedModal.styles'
import {
  LoadingTypes,
  isLoading as Loading,
  ShortenedNameV2,
} from '@labrav/common'
import {
  ButtonV2,
  TextArea,
  TextInputV2,
  VerticalDivider,
} from '@labrav/react-components'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'

import { useDispatch, useSelector } from '../../../store'

import { Product } from '../../../modules/productsListing/types'
import { Chip, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLoader,
  faPen,
  faRotateLeft,
  faCopy,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { generateContentAi } from '../../../modules/mpsSeo/action'
import { updateAppAlert } from '@labrav/common'
import { useEffect, useState } from 'react'
import { LoadingSpinnerButton } from './LoadingSpinnerButton'
import { get, isEmpty } from 'lodash'
import { requiredFieldMessage } from '../../../common/utils/messagesContants'

import { SEO_CLOUD_MARKETPLACE, useSeoAccountId } from '../seoCloud'

interface AiGeneratedModalProps {
  selectedListing: Product
  selectedSearchKeyWords: any
  handleFormChange: (formikData: {
    values: any
    errors?: Record<string, any>
    loadingStates?: {
      loadingTitle: boolean
      loadingshortDescription: boolean
      loadinglongDescription: boolean
      regenerateAllLoading: boolean
    }
  }) => void
  getData?: boolean
  handleContentButtonDisabled: (value: boolean) => void
  handleSelectedSearchKeywords: (keywords: any) => void
  handleAiModalOpen: () => void
  selectedSearchKeywordsSelection: any
  selectedListingSelection: Product
  handleCloseModal?: () => void
}

const AiGeneratedModal: React.FC<AiGeneratedModalProps> = ({
  selectedListing,
  selectedSearchKeyWords,
  handleFormChange,
  getData,
  handleContentButtonDisabled,
  handleSelectedSearchKeywords,
  handleAiModalOpen,
  selectedSearchKeywordsSelection,
  selectedListingSelection,
  handleCloseModal,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const selectedCloud = SEO_CLOUD_MARKETPLACE
  const accountId = useSeoAccountId()
  const aiGeneratedResponse = useSelector(
    state => state.marketplaceSeo[selectedCloud].aiGeneratedResponse
  )
  const isLoadingGenerateButton = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERATE_AI_LOADING)
  )
  const [loadingTitle, setLoadingTitle] = useState(false)
  const [loadingshortDescription, setLoadingshortDescription] = useState(false)
  const [loadinglongDescription, setLoadinglongDescription] = useState(false)
  const [regenerateAllLoading, setRegenerateAllLoading] = useState(false)
  const [loadingHighlightIndex, setLoadingHighlightIndex] = useState<
    number | null
  >(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
      dispatch(
        updateAppAlert({
          message: 'Copied to clipboard',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (err) {
      console.error('Failed to copy text: ', err)
      dispatch(
        updateAppAlert({
          message: 'Failed to copy to clipboard',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    }
  }

  const generateAiContent = async () => {
    setRegenerateAllLoading(true)
    await dispatch(
      generateContentAi(accountId, {
        data: selectedListing,
        keywords: selectedSearchKeyWords,
      })
    )
    setRegenerateAllLoading(false)
  }
  const handleGenerateContent = async (
    keyName: string,
    listingDataKey: string,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    formikValues?: any
  ) => {
    setLoading(true)
    await dispatch(
      generateContentAi(
        accountId,
        {
          data: {
            listingData: {
              [keyName]: (selectedListing?.listingData as any)?.[
                listingDataKey
              ],
            },
            keywords: selectedSearchKeyWords,
          },
        },
        formikValues
      )
    )
    setLoading(false)
  }
  const initialValues = {
    title: aiGeneratedResponse?.title || '',
    shortDescription: aiGeneratedResponse?.shortDescription || '',
    longDescription: aiGeneratedResponse?.longDescription || '',
    highlights: aiGeneratedResponse?.highlights || [],
  }

  const validationSchema = Yup.object({
    title: Yup.string()
      .required(requiredFieldMessage)
      .max(72, 'Title must be at most 72 characters'),
    shortDescription: Yup.string()
      .required(requiredFieldMessage)
      .max(1000, 'Short description is too long'),
    longDescription: Yup.string()
      .required(requiredFieldMessage)
      .max(5000, 'Long description is too long'),
    highlights: Yup.array().of(
      Yup.string()
        .trim(requiredFieldMessage)
        .required(requiredFieldMessage)
        .max(500, 'Each highlight must be 500 characters or less')
    ),
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={() => void 0}
      enableReinitialize
      validateOnMount={true}
    >
      {formikprops => {
        const {
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isValid,
          dirty,
          handleSubmit,
          handleReset,
          setFieldValue,
        } = formikprops
        useEffect(() => {
          if (getData) {
            handleFormChange({
              values,
              errors,
              loadingStates: {
                loadingTitle,
                loadingshortDescription,
                loadinglongDescription,
                regenerateAllLoading,
              },
            })
          }
        }, [
          getData,
          errors,
          values,
          loadingTitle,
          loadingshortDescription,
          loadinglongDescription,
          regenerateAllLoading,
        ])
        useEffect(() => {
          if (isEmpty(errors)) {
            handleContentButtonDisabled(true)
          } else {
            handleContentButtonDisabled(false)
          }
        }, [values, errors])

        return (
          <Form onSubmit={handleSubmit} onReset={handleReset}>
            <div className={classes.wrapperRoot}>
              <div className={classes.sectionWrapper}>
                <Typography className={classes.textWrapper}>
                  <ShortenedNameV2
                    name={selectedListingSelection.productName}
                    length={20}
                    showClipboardTooltip={true}
                  ></ShortenedNameV2>
                </Typography>
                <VerticalDivider height="auto" />
                <div>
                  <div className={classes.sectionWrapper}>
                    {selectedSearchKeywordsSelection.map((option: string) => (
                      <Chip
                        key={option}
                        variant="outlined"
                        className={classes.chip}
                        label={option}
                        data-testid={`filter-chip-${option}`}
                      />
                    ))}
                  </div>
                </div>

                <ButtonV2
                  styleType="link"
                  icon={<FontAwesomeIcon icon={faPen} />}
                  onClick={() => handleAiModalOpen()}
                >
                  Edit
                </ButtonV2>
              </div>
              <div>
                <div className={classes.wrapper}>
                  <div className={classes.fieldWrapper}>
                    <Typography className={classes.headingTitle}>
                      Current version
                    </Typography>{' '}
                  </div>
                  <div className={classes.fieldWrapper}>
                    <div className={classes.titleWrapper}>
                      <Typography className={classes.headingTitle}>
                        AI generated version
                      </Typography>
                      <div>
                        {!regenerateAllLoading ? (
                          <ButtonV2
                            smallVariant
                            styleType="link"
                            icon={
                              <FontAwesomeIcon
                                icon={faRotateLeft}
                                width={14}
                                height={14}
                              />
                            }
                            onClick={generateAiContent}
                          >
                            Regenerate all
                          </ButtonV2>
                        ) : (
                          <div>
                            <FontAwesomeIcon
                              icon={faLoader}
                              spin
                              width={14}
                              height={14}
                              color="#212121"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.wrapper}>
                  <div className={classes.fieldContentWrapper}>
                    <div className={classes.content}>
                      <div className={classes.titleHeight}>
                        <Typography className={classes.title}>Title</Typography>
                        <Typography className={classes.subTitle}>
                          {
                            (selectedListingSelection?.listingData as any)
                              ?.title?.value as any
                          }
                        </Typography>
                      </div>

                      <div className={classes.textAreaHeight}>
                        <Typography className={classes.title}>
                          Short description
                        </Typography>
                        <Typography className={classes.subTitle}>
                          {((selectedListingSelection?.listingData as any)
                            ?.shortDescription?.value as any) ||
                            ((selectedListingSelection?.listingData as any)
                              ?.shortProductDescription?.value as any)}
                        </Typography>
                      </div>

                      <div className={classes.longDescriptionHeight}>
                        <Typography className={classes.title}>
                          Long description
                        </Typography>
                        <Typography className={classes.subTitle}>
                          {
                            (selectedListingSelection?.listingData as any)
                              ?.productDescription?.value as any
                          }
                        </Typography>
                      </div>
                      {/* highlights */}
                      {(selectedListingSelection?.listingData as any)
                        ?.highlights &&
                        (
                          selectedListingSelection?.listingData as any
                        )?.highlights.value.map(
                          (highlight: any, index: number) => (
                            <div className={classes.textAreaHeight}>
                              <Typography className={classes.title}>
                                Highlight {index + 1}
                              </Typography>
                              <Typography className={classes.subTitle}>
                                {highlight}
                              </Typography>
                            </div>
                          )
                        )}
                    </div>
                  </div>
                  <VerticalDivider height="auto" />

                  <div className={classes.fieldContentWrapper}>
                    <div className={classes.textFieldWrapper}>
                      <div className={classes.fieldWrapperTitle}>
                        <div className={classes.width}>
                          <TextInputV2
                            keyName="title"
                            title="Title"
                            type="text"
                            placeHolder=""
                            value={values?.title}
                            updatedValue={values?.title}
                            maxCharacters={72}
                            onChange={handleChange}
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={true}
                            viewOnly={false}
                            onBlur={handleBlur}
                            isError={
                              (touched.title &&
                                Boolean(errors.title)) as boolean
                            }
                            errorText={
                              (touched.title && (errors.title as string)) || ''
                            }
                          />
                        </div>
                        <div className={classes.iconPlacement}>
                          {getData && (
                            <FontAwesomeIcon
                              icon={copiedField === 'title' ? faCheck : faCopy}
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                              style={{
                                cursor: values?.title
                                  ? 'pointer'
                                  : 'not-allowed',
                                opacity: values?.title ? 1 : 0.4,
                              }}
                              onClick={() => {
                                if (values?.title) {
                                  handleCopy(values.title, 'title')
                                }
                              }}
                            />
                          )}
                          {loadingTitle || regenerateAllLoading ? (
                            <FontAwesomeIcon
                              icon={faLoader}
                              spin
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                            />
                          ) : (
                            <LoadingSpinnerButton
                              onClick={() =>
                                handleGenerateContent(
                                  'title',
                                  'title',
                                  setLoadingTitle,
                                  values
                                )
                              }
                              isLoading={loadingTitle}
                              allLoading={regenerateAllLoading}
                            />
                          )}
                        </div>
                      </div>
                      <div className={classes.fieldWrapperTitle}>
                        <div className={classes.width}>
                          <TextArea
                            keyName="shortDescription"
                            title="Short description"
                            rows={10}
                            maxCharacters={1000}
                            height={100}
                            type="string"
                            placeHolder=""
                            value={values.shortDescription}
                            updatedValue={values.shortDescription}
                            onChange={handleChange}
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            viewOnly={false}
                            onBlur={handleBlur}
                            isError={
                              (touched.shortDescription &&
                                Boolean(errors.shortDescription)) as boolean
                            }
                            errorText={
                              (touched.shortDescription &&
                                (errors.shortDescription as string)) ||
                              ''
                            }
                          />
                        </div>
                        <div className={classes.iconPlacement}>
                          {getData && (
                            <FontAwesomeIcon
                              icon={
                                copiedField === 'shortDescription'
                                  ? faCheck
                                  : faCopy
                              }
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                              style={{
                                cursor: values?.shortDescription
                                  ? 'pointer'
                                  : 'not-allowed',
                                opacity: values?.shortDescription ? 1 : 0.4,
                              }}
                              onClick={() => {
                                if (values?.shortDescription) {
                                  handleCopy(
                                    values.shortDescription,
                                    'shortDescription'
                                  )
                                }
                              }}
                            />
                          )}
                          {loadingshortDescription || regenerateAllLoading ? (
                            <FontAwesomeIcon
                              icon={faLoader}
                              spin
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                            />
                          ) : (
                            <LoadingSpinnerButton
                              onClick={() =>
                                handleGenerateContent(
                                  'shortProductDescription',
                                  'shortProductDescription',
                                  setLoadingshortDescription,
                                  values
                                )
                              }
                              isLoading={loadingshortDescription}
                              allLoading={regenerateAllLoading}
                            />
                          )}
                        </div>
                      </div>
                      <div className={classes.fieldWrapperTitle}>
                        <div className={classes.width}>
                          <TextArea
                            keyName="longDescription"
                            title="Long description"
                            rows={10}
                            maxCharacters={5000}
                            height={240}
                            type="string"
                            placeHolder=""
                            value={values.longDescription}
                            updatedValue={values.longDescription}
                            onChange={handleChange}
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            viewOnly={false}
                            onBlur={handleBlur}
                            isError={
                              (touched.longDescription &&
                                Boolean(errors.longDescription)) as boolean
                            }
                            errorText={
                              (touched.longDescription &&
                                (errors.longDescription as string)) ||
                              ''
                            }
                          />
                        </div>
                        <div className={classes.iconPlacement}>
                          {getData && (
                            <FontAwesomeIcon
                              icon={
                                copiedField === 'longDescription'
                                  ? faCheck
                                  : faCopy
                              }
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                              style={{
                                cursor: values?.longDescription
                                  ? 'pointer'
                                  : 'not-allowed',
                                opacity: values?.longDescription ? 1 : 0.4,
                              }}
                              onClick={() => {
                                if (values?.longDescription) {
                                  handleCopy(
                                    values.longDescription,
                                    'longDescription'
                                  )
                                }
                              }}
                            />
                          )}
                          {loadinglongDescription || regenerateAllLoading ? (
                            <FontAwesomeIcon
                              icon={faLoader}
                              spin
                              width={14}
                              height={14}
                              className={classes.actionIcon}
                            />
                          ) : (
                            <LoadingSpinnerButton
                              onClick={() =>
                                handleGenerateContent(
                                  'productDescription',
                                  'productDescription',
                                  setLoadinglongDescription,
                                  values
                                )
                              }
                              isLoading={loadinglongDescription}
                              allLoading={regenerateAllLoading}
                            />
                          )}
                        </div>
                      </div>
                      {values.highlights.map(
                        (highlight: string, index: number) => (
                          <div className={classes.fieldWrapperTitle}>
                            <div
                              key={`highlights.${index}`}
                              className={classes.width}
                            >
                              <TextArea
                                keyName={`highlights.${index}`}
                                title={`Highlight ${index + 1}`}
                                rows={10}
                                maxCharacters={500}
                                height={100}
                                type="string"
                                placeHolder=""
                                value={highlight}
                                updatedValue={highlight}
                                onChange={handleChange}
                                isReadOnly={false}
                                isRequired={false}
                                showSpinner={false}
                                viewOnly={false}
                                onBlur={handleBlur}
                                isError={
                                  !!(
                                    get(
                                      touched,
                                      `highlights.${[index]}`,
                                      false
                                    ) &&
                                    get(errors, `highlights.${[index]}`, false)
                                  )
                                }
                                errorText={
                                  (!!get(
                                    touched,
                                    `highlights.${index}`,
                                    false
                                  ) &&
                                    String(
                                      get(errors, `highlights.${index}`, ' ')
                                    )) ||
                                  ' '
                                }
                              />
                            </div>
                            <div className={classes.iconPlacement}>
                              {getData && (
                                <FontAwesomeIcon
                                  icon={
                                    copiedField === `highlight-${index}`
                                      ? faCheck
                                      : faCopy
                                  }
                                  width={14}
                                  height={14}
                                  className={classes.actionIcon}
                                  style={{
                                    cursor: highlight
                                      ? 'pointer'
                                      : 'not-allowed',
                                    opacity: highlight ? 1 : 0.4,
                                  }}
                                  onClick={() => {
                                    if (highlight) {
                                      handleCopy(
                                        highlight,
                                        `highlight-${index}`
                                      )
                                    }
                                  }}
                                />
                              )}
                              {loadingHighlightIndex === index ||
                              regenerateAllLoading === true ? (
                                <FontAwesomeIcon
                                  icon={faLoader}
                                  spin
                                  width={14}
                                  height={14}
                                  className={classes.actionIcon}
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faRotateLeft}
                                  width={14}
                                  height={14}
                                  className={classes.actionIcon}
                                  onClick={async () => {
                                    setLoadingHighlightIndex(index)
                                    const highlight = [
                                      (selectedListing?.listingData as any)
                                        ?.highlights.value[index],
                                    ]
                                    await dispatch(
                                      generateContentAi(
                                        accountId,
                                        {
                                          data: {
                                            listingData: {
                                              highlights: { value: highlight },
                                            },
                                            keywords: selectedSearchKeyWords,
                                          },
                                        },
                                        values,
                                        index
                                      )
                                    )
                                    setLoadingHighlightIndex(null)
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default AiGeneratedModal
